import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { ChipColour } from '../../../../chip/chip.component';
import { DryEyeFormGroup } from '../../dry-eye-form/dry-eye-form.model';
import { getIdentifiedDrivers } from '../dews-iii-drivers/dews-iii-drivers.component';
import { DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY } from '../dews-iii-medical-history/dews-iii-medical-history.component';
import { DEWS_MEDICAL_HISTORY_FIELDS } from '../dews-iii-medical-history/dews-iii-medical-history.schema';
import {
  DewsIIIService,
  DiagnosisResult,
  HomeostasisResult,
  SymptomClassification
} from '../dews-iii.service';

@Component({
  selector: 'csi-dews-iii-diagnosis',
  templateUrl: 'dews-iii-diagnosis.component.html',
  styleUrls: ['dews-iii-diagnosis.component.scss']
})
export class DewsIIIDiagnosisComponent implements OnInit, OnDestroy {
  @Input() formGroup: DryEyeFormGroup;
  @Input() onQuestionnaireLinked$: Observable<any>;

  diagnosisResult: DiagnosisResult;
  osdiScore: number;
  symptomClassification: SymptomClassification;
  clinicianOverride = false;

  isInsufficientForDiagnosis = true;
  diagnosisBannerClass = 'banner-unknown';
  diagnosisLabel = 'Awaiting Data';
  symptomClassificationLabel = 'No OSDI-6 Score';
  symptomClassificationChipColour: ChipColour = 'grey-no-outline';
  discordanceGuidance = '';
  symptomsPresent = false;
  identifiedDrivers: string[] = [];
  medicalHistoryFlags: string[] = [];

  constructor(private dewsIIIService: DewsIIIService) {}

  ngOnInit() {
    this.ensureControls();
    this.recalculate();
    this.watchChanges();

    // Re-evaluate when questionnaires are linked
    if (this.onQuestionnaireLinked$) {
      this.onQuestionnaireLinked$.pipe(untilDestroyed(this)).subscribe(() => {
        this.recalculate();
      });
    }
  }

  ngOnDestroy() {}

  private ensureControls() {
    if (!this.formGroup.controls['osdi6']) {
      this.formGroup.addControl(
        'osdi6',
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
    }
    if (!this.formGroup.controls['dewsDedDiagnosed']) {
      this.formGroup.addControl(
        'dewsDedDiagnosed',
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
    }
    if (!this.formGroup.controls['dewsDiscordanceType']) {
      this.formGroup.addControl(
        'dewsDiscordanceType',
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
    }
    if (!this.formGroup.controls['dewsClinicianOverride']) {
      this.formGroup.addControl(
        'dewsClinicianOverride',
        new FormControl({ value: false, disabled: this.formGroup.disabled })
      );
    }
    if (!this.formGroup.controls['dewsDiagnosisNotes']) {
      this.formGroup.addControl(
        'dewsDiagnosisNotes',
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
    }
  }

  private watchChanges() {
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.recalculate());

    const overrideControl = this.formGroup.controls['dewsClinicianOverride'];
    if (overrideControl) {
      overrideControl.valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
        this.clinicianOverride = value;
      });
    }
  }

  recalculate() {
    this.osdiScore = this.getOsdiScore();
    this.symptomClassification = this.dewsIIIService.classifyOsdiScore(this.osdiScore);
    this.symptomsPresent = this.symptomClassification !== 'normal';

    const homeostasisResults = this.getHomeostasisResults();
    this.diagnosisResult = this.dewsIIIService.determineDiagnosis(
      this.osdiScore,
      homeostasisResults
    );
    this.isInsufficientForDiagnosis =
      !this.diagnosisResult || this.diagnosisResult.isInsufficientToRuleOut;

    if (this.formGroup.controls['dewsDedDiagnosed']) {
      const newDed = this.diagnosisResult.dedDiagnosed;
      if (this.formGroup.controls['dewsDedDiagnosed'].value !== newDed) {
        this.formGroup.controls['dewsDedDiagnosed'].setValue(newDed);
      }
    }
    if (this.formGroup.controls['dewsDiscordanceType']) {
      const newDiscordance = this.diagnosisResult.discordanceType;
      if (this.formGroup.controls['dewsDiscordanceType'].value !== newDiscordance) {
        this.formGroup.controls['dewsDiscordanceType'].setValue(newDiscordance);
      }
    }

    this.diagnosisBannerClass = this.dewsIIIService.diagnosisBannerClass(this.diagnosisResult);
    this.diagnosisLabel = this.dewsIIIService.diagnosisLabel(this.diagnosisResult, this.osdiScore);
    this.symptomClassificationLabel = this.dewsIIIService.symptomClassificationLabel(
      this.osdiScore
    );
    this.symptomClassificationChipColour = this.computeSymptomClassificationChipColour();
    // this.discordanceGuidance = this.computeDiscordanceGuidance();
    this.identifiedDrivers = this.computeIdentifiedDrivers();
    this.medicalHistoryFlags = this.computeMedicalHistoryFlags();
    this.discordanceGuidance = this.dewsIIIService.discordanceGuidance(this.diagnosisResult);
  }

  private getOsdiScore(): number {
    const control = this.formGroup.controls['osdi6'];
    if (control && control.value !== null && control.value !== undefined) {
      return control.value;
    }
    return null;
  }

  private getHomeostasisResults(): HomeostasisResult[] {
    const control = this.formGroup.controls['dewsHomeostasisResults'];
    if (control && control.value && Array.isArray(control.value)) {
      return control.value;
    }
    return [
      { category: 'tearFilmStability', performed: false, positive: false },
      { category: 'osmolarity', performed: false, positive: false },
      { category: 'ocularSurfaceStaining', performed: false, positive: false }
    ];
  }

  toggleClinicianOverride() {
    const current = this.formGroup.controls['dewsClinicianOverride'].value;
    this.formGroup.controls['dewsClinicianOverride'].setValue(!current);
  }

  private computeSymptomClassificationChipColour(): ChipColour {
    switch (this.symptomClassification) {
      case 'normal':
        return 'green-no-outline';
      case 'mildToModerate':
        return 'orange';
      case 'severe':
        return 'red-no-outline';
      default:
        return 'grey-no-outline';
    }
  }

  private computeIdentifiedDrivers(): string[] {
    return getIdentifiedDrivers(this.formGroup.controls);
  }

  private computeMedicalHistoryFlags(): string[] {
    const nestedGroup = this.formGroup.controls[DEWS_DIFFERENTIAL_DIAGNOSIS_FORM_KEY] as FormGroup;
    const controls = nestedGroup ? nestedGroup.controls : {};
    return DEWS_MEDICAL_HISTORY_FIELDS
      .filter(f => controls[f.key] && controls[f.key].value === f.expected)
      .map(f => f.flagLabel);
  }
}
